import { BaseBadge, Card, FeaturedIcon, MarketIntelligenceIcon, RuleTraitIcon, RuleViewIcon } from '@/lib';
import { MarketIntelligenceCompetitor, MarketIntelligenceReportData } from '../../types';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

const competitorIcons = [MarketIntelligenceIcon, RuleViewIcon, RuleTraitIcon];

const CompetitorListItem: FC<{ competitor: MarketIntelligenceCompetitor; index: number; threatLabel: string }> = ({ competitor, index, threatLabel }) => {
  const Icon = competitorIcons[index % competitorIcons.length];

  return (
    <Card className='items-center gap-4 border-primary-border bg-white p-4 drop-shadow-none'>
      <FeaturedIcon color='gray' size='sm' type='modern'>
        <Icon className='size-4' />
      </FeaturedIcon>
      <div className='min-w-0 flex-1'>
        <p className='text-primary-900 truncate text-sm font-semibold'>{competitor.name}</p>
        <p className='text-primary-600 mt-1 text-xs leading-5'>{competitor.description}</p>
      </div>
      {typeof competitor.threat === 'number' ? (
        <div className='shrink-0 text-right'>
          <p className='text-2xl font-semibold leading-none text-brand-700'>{competitor.threat}</p>
          <p className='text-primary-500 mt-1 text-[10px] font-semibold uppercase tracking-[0.08em]'>{threatLabel}</p>
        </div>
      ) : null}
    </Card>
  );
};

export const Competitors = ({ report }: { report: MarketIntelligenceReportData }) => {
  const { t } = useTranslation('marketIntelligence');

  return (
    <Card className='block border-primary-border bg-white p-5 drop-shadow-none'>
      <div className='flex items-start justify-between gap-3'>
        <div>
          <h3 className='text-2xl font-semibold text-primary-900'>{t('report.competitors.title')}</h3>
          <p className='mt-1 text-sm text-grey-600'>{t('report.competitors.description')}</p>
        </div>
        <BaseBadge className='rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-700'>
          {t('report.competitors.badge')}
        </BaseBadge>
      </div>

      <div className='mt-5 space-y-3'>
        {report.competitors.map((competitor, index) => (
          <CompetitorListItem key={competitor.id} competitor={competitor} index={index} threatLabel={t('report.competitors.threat')} />
        ))}
      </div>
    </Card>
  );
};
